import React, { useState } from 'react';
import { useT } from '../i18n';

const LoadRaceFromAnubeModal = ({ initialConfig, onApply, onClose }) => {
  const t = useT();
  const [raceInput, setRaceInput] = useState(String(initialConfig.raceNumber));
  const [allSpecialsByItinerary, setAllSpecialsByItinerary] = useState({});
  const [itineraries, setItineraries] = useState([]);
  const [specials, setSpecials] = useState([]);
  const [selectedItinerary, setSelectedItinerary] = useState('');
  const [selectedSpecial, setSelectedSpecial] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fetchedEventName, setFetchedEventName] = useState('');
  const [priorityIdents, setPriorityIdents] = useState(initialConfig.priorityIdents || '');
  const [useFiaRules, setUseFiaRules] = useState(initialConfig.useFiaRules || false);
  const [maxParticipants, setMaxParticipants] = useState(initialConfig.maxParticipants ?? 10);

  const handleLoad = async () => {
    if (!raceInput) return;
    setLoading(true);
    setError('');
    try {
      const [data, raceData] = await Promise.all([
        fetch(`https://rest3.anube.es/rallyrest/timing/api/specials/${raceInput}.json`).then(r => r.json()),
        fetch(`https://rest3.anube.es/rallyrest/timing/api/race/${raceInput}.json?add_subrallies_races=1`).then(r => r.json()),
      ]);
      const name = raceData?.event?.name || raceData?.event?.data?.name || '';
      setFetchedEventName(name);
      const itinList = data.event.data.itineraries;
      setItineraries(itinList);
      const byId = {};
      itinList.forEach(it => { byId[it.id] = it.specials; });
      setAllSpecialsByItinerary(byId);
      const firstItin = itinList[0];
      if (firstItin) {
        const itinId = byId[initialConfig.itineraryId] ? initialConfig.itineraryId : firstItin.id;
        const specsForItin = byId[itinId] || [];
        const validSpecial = specsForItin.find(s => s.id === initialConfig.specialId);
        const specialId = validSpecial ? validSpecial.id : (specsForItin[0]?.id ?? '');
        setSelectedItinerary(String(itinId));
        setSpecials(specsForItin);
        setSelectedSpecial(String(specialId));
      }
    } catch {
      setError('Failed to load. Check the race number.');
    }
    setLoading(false);
  };

  const handleItineraryChange = (itinId) => {
    setSelectedItinerary(itinId);
    const specs = allSpecialsByItinerary[Number(itinId)] || [];
    setSpecials(specs);
    setSelectedSpecial(specs[0] ? String(specs[0].id) : '');
  };

  const handleApply = () => {
    if (!selectedSpecial || !selectedItinerary) return;
    onApply({ raceNumber: raceInput, itineraryId: Number(selectedItinerary), specialId: Number(selectedSpecial), eventName: fetchedEventName, priorityIdents, useFiaRules, maxParticipants: Number(maxParticipants) });
    onClose();
  };

  const inpStyle = {
    background: 'transparent', border: 'none',
    borderBottom: '1px solid #aaa', outline: 'none',
    fontFamily: 'Verdana', fontSize: '1em', color: '#222',
    padding: '2px 4px', width: '100%',
  };
  const selStyle = {
    width: '100%', background: '#f9f9f9', border: '1px solid #ccc',
    borderRadius: '4px', outline: 'none',
    fontFamily: 'Verdana', fontSize: '0.9em', color: '#222',
    padding: '4px 6px', cursor: 'pointer',
  };
  const labelStyle = { fontSize: '0.72em', color: '#888', display: 'block', marginBottom: '4px', marginTop: '0.9rem' };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: '#fff', borderRadius: '12px', padding: '1.5rem 1.75rem',
        width: '600px', maxWidth: '92vw',
        boxShadow: '0 8px 32px rgba(0,0,0,0.55)',
        fontFamily: 'Verdana', color: '#222',
      }} onClick={e => e.stopPropagation()}>
        <h5 style={{ margin: '0 0 1.2rem', fontFamily: 'Verdana', fontSize: '1.05em', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <img src='img/logo-anube.svg' alt='Anube' style={{ height: '1.4em', filter: 'brightness(0) saturate(100%) invert(11%) sepia(97%) saturate(6074%) hue-rotate(352deg) brightness(91%) contrast(112%)' }} />
          {t('loadFromAnubeTitle')}
        </h5>

        <label style={labelStyle}>{t('raceNumber')}</label>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input
            value={raceInput}
            onChange={e => setRaceInput(e.target.value)}
            placeholder={t('raceNumberPlaceholder')}
            style={{ ...inpStyle, width: '6em' }}
            onKeyDown={e => e.key === 'Enter' && handleLoad()}
            autoFocus
          />
          <button onClick={handleLoad} disabled={loading} style={{
            background: '#4488cc', border: 'none', borderRadius: '6px',
            color: 'white', padding: '0.35em 0.9em', cursor: loading ? 'default' : 'pointer',
            fontFamily: 'Verdana', fontSize: '0.9em', opacity: loading ? 0.6 : 1, flexShrink: 0,
          }}>{loading ? t('loading') : t('load')}</button>
        </div>

        {error && <div style={{ color: '#cc4444', fontSize: '0.8em', marginTop: '0.5rem' }}>{t('loadError')}</div>}

        {fetchedEventName !== '' && (
          <div style={{ marginTop: '0.9rem' }}>
            <label style={labelStyle}>{t('eventName')}</label>
            <input
              value={fetchedEventName}
              onChange={e => setFetchedEventName(e.target.value)}
              style={inpStyle}
            />
          </div>
        )}

        {itineraries.length >= 0 && (
          <>
            <label style={labelStyle}>{t('itinerary')}</label>
            <select value={selectedItinerary} onChange={e => handleItineraryChange(e.target.value)} style={selStyle}>
              {itineraries.map(it => (
                <option key={it.id} value={String(it.id)}>{it.name}</option>
              ))}
            </select>

            <label style={labelStyle}>{t('specialStage')}</label>
            <select value={selectedSpecial} onChange={e => setSelectedSpecial(e.target.value)} style={selStyle}>
              {specials.map(s => (
                <option key={s.id} value={String(s.id)}>{s.special_name} — {s.name_extra}</option>
              ))}
            </select>

            <div style={{ marginTop: '0.9rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type='checkbox'
                id='fia-rules'
                checked={useFiaRules}
                onChange={e => setUseFiaRules(e.target.checked)}
                style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#4488cc' }}
              />
              <label htmlFor='fia-rules' style={{ fontSize: '0.88em', color: '#222', cursor: 'pointer', margin: 0 }}>{t('useFiaRules')}</label>
            </div>

            <label style={{ ...labelStyle, opacity: useFiaRules ? 0.4 : 1 }}>{t('maxParticipantsLabel')}</label>
            <select
              value={maxParticipants}
              onChange={e => setMaxParticipants(e.target.value)}
              disabled={useFiaRules}
              style={{ ...selStyle, width: '6em', opacity: useFiaRules ? 0.4 : 1, cursor: useFiaRules ? 'not-allowed' : 'pointer' }}
            >
              {Array.from({ length: 50 }, (_, i) => i + 1).map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>

            <label style={labelStyle}>{t('priorityPilotsLabel')}</label>
            <input
              value={priorityIdents}
              onChange={e => setPriorityIdents(e.target.value)}
              placeholder={t('priorityPilotsPlaceholder')}
              style={inpStyle}
            />
            <div style={{ fontSize: '0.72em', color: '#aaa', marginTop: '3px' }}>{t('priorityPilotsHint')}</div>
          </>
        )}

        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
          <button onClick={onClose} style={{
            background: 'none', border: '1px solid #bbb', borderRadius: '6px',
            padding: '0.4em 1.1em', cursor: 'pointer', fontFamily: 'Verdana', fontSize: '0.9em',
          }}>{t('cancel')}</button>
          <button
            onClick={handleApply}
            disabled={!selectedSpecial}
            style={{
              background: selectedSpecial ? '#00aa55' : '#aaa', border: 'none', borderRadius: '6px',
              color: 'white', padding: '0.4em 1.1em', cursor: selectedSpecial ? 'pointer' : 'default',
              fontFamily: 'Verdana', fontSize: '0.9em',
            }}>{t('applyAndFetch')}</button>
        </div>
      </div>
    </div>
  );
};

export default LoadRaceFromAnubeModal;
